import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {FormArray, FormBuilder, Validators} from "@angular/forms";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {CommunicationRestService} from "../../../../core/communication/api/communication.rest-service";
import {Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {GermanMobileNumberValidator} from "../../../../core/validators/german-mobile-number.validator";
import {allFieldsEmpty} from "../../../../core/validators/all-fields-empty.validator";

@Component({
  selector: 'app-communication-dialog',
  templateUrl: './communication-dialog.component.html',
  styleUrl: './communication-dialog.component.scss',
  standalone: false,
})
export class CommunicationDialogComponent implements OnInit, OnDestroy {
  public ngUnsubscribe$: Subject<void> = new Subject<void>();
  public emailList: FormArray;
  public mobileList: FormArray;
  communicationForm: any;

  constructor(
    public dialogRef: MatDialogRef<CommunicationDialogComponent>,
    private formBuilder: FormBuilder,
    private communicationRestService: CommunicationRestService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.emailList = this.formBuilder.array([]);
    this.mobileList = this.formBuilder.array([]);
  }

  ngOnInit() {
    this.communicationForm = this.formBuilder.group({
      emails: this.emailList,
      phoneNumbers: this.mobileList
    }, {validator: allFieldsEmpty});
    if (this.data['config']) {
      this.emailList.clear();
      this.mobileList.clear();
      const selected = this.data['config'];
      const selectedEmails = this.getConfigEmails(selected);
      const formData: { emails: { email: string }[], phoneNumbers: { mobile: string }[] } = {
        emails: [],
        phoneNumbers: []
      };

      for (const email of selectedEmails) {
        this.addEmail();
        formData.emails.push({email: email});
      }

      const phoneNumbers = selected.phoneNumbers ?? selected.phone_numbers ?? [];
      for (const mobile of phoneNumbers) {
        formData.phoneNumbers.push({mobile: mobile});
        this.addMobile();
      }

      if (selectedEmails.length === 0) {
        this.addEmail();
        const defaultEmail = this.getDefaultEmail();
        if (this.isTrackTrace() && defaultEmail) {
          formData.emails.push({email: defaultEmail});
        }
      }

      if (phoneNumbers.length === 0) {
        this.addMobile();
      }

      this.communicationForm.patchValue(formData);
    } else {
      this.emailList.push(this.createEmail());
      this.mobileList.push(this.createMobile());
    }
  }

  createEmail() {
    return this.formBuilder.group({email: ['', Validators.email]});
  }

  createMobile() {
    return this.formBuilder.group({mobile: ['', GermanMobileNumberValidator()]});
  }

  addEmail() {
    this.emailList.push(this.createEmail());
  }

  addMobile() {
    this.mobileList.push(this.createMobile());
  }

  onSubmit() {
    const values = this.communicationForm.value;
    values['type'] = this.data['type'].value;
    values['active'] = true;
    values['emails'] = values['emails']
      .map((val: { email: string }) => val.email)
      .filter((entry: string) => entry.trim() !== '');

    if (!this.ensureTrackTraceEmail(values)) {
      return;
    }

    values['phone_numbers'] = values['phoneNumbers']
      .map((val: { mobile: string }) => val.mobile)
      .filter((entry: string) => entry.trim() !== '');

    delete values['phoneNumbers'];

    if (!(values['emails'].length === 0 && values['phone_numbers'].length === 0)) {
      this.communicationRestService.saveConfig(this.data['idf'], values)
        .pipe(takeUntil(this.ngUnsubscribe$))
        .subscribe({
          next: result => {
            this.dialogRef.close(
              result.error_code
                ? {success: false, errorCode: result.error_code}
                : {success: true, config: values}
            );
          },
          error: () => {
            this.dialogRef.close({success: false, errorCode: 'NETWORK'});
          }
        });
    }
  }

  onCancel() {
    this.dialogRef.close({success: false, errorCode: null});
    this.communicationForm.reset();
  }

  removeEmail(i: number) {
    this.emailList.removeAt(i);
  }

  removeMobile(i: number) {
    this.mobileList.removeAt(i);
  }

  private isTrackTrace(): boolean {
    return this.data?.['type']?.value === 'TRACK_TRACE';
  }


  private ensureTrackTraceEmail(values: any): boolean {
    if (!this.isTrackTrace() || values['emails'].length > 0) {
      this.clearTrackTraceEmailError();
      return true;
    }

    if (this.emailList.length === 1) {
      const defaultEmail = this.getDefaultEmail();
      if (defaultEmail) {
        values['emails'] = [defaultEmail];
        this.clearTrackTraceEmailError();
        return true;
      }
    }

    this.communicationForm.get('emails')?.markAsTouched();
    this.communicationForm.get('emails')?.setErrors({trackTraceEmailRequired: true});
    return false;
  }

  private clearTrackTraceEmailError(): void {
    const emailsControl = this.communicationForm.get('emails');
    const errors = emailsControl?.errors;
    if (!errors?.['trackTraceEmailRequired']) {
      return;
    }

    const nextErrors = {...errors};
    delete nextErrors['trackTraceEmailRequired'];
    emailsControl?.setErrors(Object.keys(nextErrors).length ? nextErrors : null);
  }

  private getConfigEmails(config: any): string[] {
    const emails = config?.emails ?? config?.email_addresses ?? config?.emailAddresses ?? [];
    return (emails as string[])
      .map((email: string) => email.trim())
      .filter((email: string) => email.length > 0);
  }

  private getDefaultEmail(): string | null {
    const email = this.data?.['defaultEmail']?.trim();
    return email ? email : null;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

}
